import React, { useContext, useMemo, useState } from 'react';
import { Option } from 'react-bootstrap-typeahead/types/types';

import { derapiJoinVendors, IDerapiVendor } from '../../configs/DerapiVendorConfig';
import { ZeosApiContext } from '../../context/ZeosApiContext';
import { IPortfolioItem } from '../../mock-data/portfolioData';
import { IZeosUser } from '../../services/auth/IZeosUser';
import { isZeosEnvironment } from '../../services/environment/EnvironmentHelpers';
import { createLogger } from '../../services/Logger';
import { IDerapiJoinFormRequest } from '../../services/zeos-api/contracts/Derapi';
import { ZeosButton } from '../Shared/ZeosButton';
import { ZeosSpinner } from '../Shared/ZeosSpinner';
import { ZeosTypeAhead } from '../Shared/ZeosTypeAhead';

interface IProps {
  property: IPortfolioItem | null;
  zeosUser: IZeosUser | undefined;
  onClose: () => void;
}

interface IDerapiVendorOption {
  label: string;
  vendorCode: string;
}

const log = createLogger("DerapiLinkSelector");

export const DerapiLinkSelector = (props: IProps) => {
  const api = useContext(ZeosApiContext);
  const [selectedVendorCode, setSelectedVendorCode] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Non-prod only: lets us exercise the flow against Derapi's virtual vendor without a real
  // account. Never shown to a real customer.
  const vendorOptions = useMemo(() => {
    const vendors: IDerapiVendor[] = isZeosEnvironment("prod")
      ? derapiJoinVendors
      : [...derapiJoinVendors, { vendorCode: "virtual", vendorName: "Virtual (test)" }];

    return vendors.map(
      (vendor) =>
        ({
          label: vendor.vendorName,
          vendorCode: vendor.vendorCode,
        } as IDerapiVendorOption)
    );
  }, []);

  const handleVendorSelection = (selected: Option[]) => {
    if (selected.length > 0) {
      setSelectedVendorCode((selected[0] as IDerapiVendorOption).vendorCode);
    } else {
      setSelectedVendorCode("");
    }
  };

  const handleConnect = async () => {
    const zeosUserId = props.zeosUser?.zeosUserId ?? 0;
    const propertyId = props.property?.propertyId ?? 0;

    if (!zeosUserId || !propertyId || !selectedVendorCode) {
      setError("Select a vendor to continue.");
      return;
    }

    const request: IDerapiJoinFormRequest = {
      zeosUserId,
      propertyId,
      vendor: selectedVendorCode,
    };

    try {
      setError(null);
      setIsConnecting(true);
      const response = await api.Derapi.createJoinForm(request);
      const authorizeUrl = response.data?.authorizeUrl;

      if (!authorizeUrl) {
        setError("Unable to start the authorization. Please try again.");
        setIsConnecting(false);
        return;
      }

      // Full-page redirect: Derapi's own join-callback is what confirms completion
      // and lands the user back on the portfolio, so there is nothing to poll here.
      window.location.href = authorizeUrl;
    } catch (err) {
      log.error("Failed to start Derapi join flow", err);
      setError(
        "Something went wrong while starting the connection. Please try again."
      );
      setIsConnecting(false);
    }
  };

  if (isConnecting) {
    return (
      <ZeosSpinner message="Redirecting you to authorize your account..." />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <div className="text-sm text-red-600" role="alert">
          {error}
        </div>
      )}
      <ZeosTypeAhead
        label="Select Power Generation Provider"
        options={vendorOptions}
        placeholder="Search and select a provider..."
        onChange={handleVendorSelection}
        clearable={true}
      />
      <div className="flex gap-2">
        <ZeosButton
          label="Connect"
          type="button"
          mode="action"
          onPress={handleConnect}
          isDisabled={!selectedVendorCode}
        />
        <ZeosButton
          label="Cancel"
          type="button"
          mode="prev"
          onPress={props.onClose}
        />
      </div>
    </div>
  );
};
